import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import customDate from "../../../utils/customDate";
import customTime from "../../../utils/customTime";
import {
  AccessTime,
  ArrowBack,
  CheckCircle,
  FlightLand,
  FlightTakeoff,
  Luggage,
  MonetizationOn,
  Refresh,
  Restaurant,
  Shield,
  Star,
} from "@mui/icons-material";
import { formatNumber } from "../../../utils/convertToNumber";

const PriceWithAncillaries = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const searchParams = location.state?.searchParams;
  const selectedBundle = location.state?.selectedBundle;
  const bundlePricing = location.state?.response;
  const selectedOutboundBundle = location.state?.selectedOutboundBundle;
  const selectedInboundBundle = location.state?.selectedInboundBundle;
  const queryURL = location.state?.queryURL;
  const charges = location.state?.charges;
  const originalPath = location.state?.originalPath;
  const currency = location.state?.currency;

  const handleProceedToBooking = () => {
    navigate(`/airarabia-booking${queryURL}`, {
      state: {
        searchParams,
        price: bundlePricing,
        sessionInfo: bundlePricing?.sessionInfo,
        selectedBundle,
        bundlePricing: true,
        queryURL,
        charges,
        currency,
      },
    });
  };

  const getBundleIcon = (service) => {
    switch (service) {
      case "BAGGAGE":
        return <Luggage className="w-4 h-4" />;
      case "SEAT_MAP":
        return <CheckCircle className="w-4 h-4" />;
      case "MEAL":
        return <Restaurant className="w-4 h-4" />;
      case "FLEXI_CHARGES":
        return <Shield className="w-4 h-4" />;
      default:
        return <Star className="w-4 h-4" />;
    }
  };

  const formatCurrency = (amount, currency) => {
    if (amount === 0 || amount < 0.01) {
      return "Free";
    }
    return `${currency} ${amount?.toFixed(2)}`;
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <div className="flex items-center space-x-4">
          <div className="w-12 h-12 rounded-full border-b-2 animate-spin border-main"></div>
          <span className="text-lg text-gray-600">
            Loading bundle pricing...
          </span>
        </div>
      </div>
    );
  }

  if (error || !bundlePricing) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <div className="text-center">
          <h2 className="mb-2 text-xl font-medium text-gray-800">
            {error || "Unable to load bundle pricing"}
          </h2>
          <p className="mb-4 text-gray-600">Please try again later.</p>
          <button
            onClick={() => navigate(-1)}
            className="px-6 py-2 text-white bg-blue-600 rounded-lg transition-colors hover:bg-blue-700"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const flightSegments = bundlePricing?.flights;
  const bundleServices = bundlePricing?.bundles || [];
  const pricingInfo = bundlePricing.pricing?.totals;

  const selectedBundleInfo =
    bundleServices?.find(
      (bundle) =>
        bundle?.id ===
          (originalPath.length > 1
            ? selectedOutboundBundle?.id
            : selectedBundle?.id) && bundle?.applicableOndSequence === "0"
    ) || selectedBundle;

  const selectedInboundBundleInfo =
    bundleServices?.find(
      (bundle) =>
        originalPath.length > 1 &&
        bundle?.id === selectedInboundBundle?.id &&
        bundle?.applicableOndSequence === "1"
    ) || selectedInboundBundle;

  const selectedBundles = [
    selectedBundleInfo,
    selectedInboundBundleInfo,
  ].filter((bundle) => bundle !== null && bundle !== undefined);

  const totalPrice = () => {
    const total =
      bundlePricing?.pricing?.totals?.totalFare?.amount + (charges || 0);

    return Number(total.toFixed(2));
  };

  const totalTax = bundlePricing?.pricing?.passengers
    ?.reduce((acc, item) => {
      const taxSum =
        item.taxes?.reduce((taxAcc, tax) => taxAcc + Number(tax.amount), 0) ||
        0;
      const quantity = item.passengerType?.quantity || 1;
      return Number(acc + taxSum * quantity);
    }, 0)
    .toFixed(2);

  return (
    <div className="px-2 py-4 min-h-scree">
      <div className="mx-auto max-w-6xl">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center space-x-2 transition-colors text-main"
        >
          <ArrowBack className="w-5 h-5" />
          <span>Back to Flight Details</span>
        </button>

        <div className="mb-3 text-center">
          <h1 className="mb-2 text-3xl font-semibold text-gray-900">
            Ancillaries Pricing Confirmation
          </h1>
          <p className="text-lg text-gray-600">
            Review your selected ancillaries and pricing details
          </p>
        </div>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <div className="space-y-3 lg:col-span-2">
            <div className="p-4 bg-white rounded-2xl border border-gray-200 shadow-sm">
              <h2 className="flex items-center mb-2 text-xl font-semibold text-gray-900">
                <FlightTakeoff className="mr-2 w-5 h-5 text-main" />
                Flight Summary
              </h2>

              {flightSegments?.map((flight, index) => (
                <div
                  key={index}
                  className="p-4 mb-4 rounded-lg bg-amber-50/30 last:mb-0"
                >
                  <div className="flex justify-between items-center">
                    <div>
                      <h3 className="text-lg font-medium text-gray-900">
                        Flight {flight?.flightNumber}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {flight?.departureDateTime &&
                          customDate(flight?.departureDateTime)}
                      </p>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-gray-600">
                        {flight?.departureAirport?.locationCode ||
                          flight?.departureAirport?.code ||
                          flight?.origin}{" "}
                        →{" "}
                        {flight?.arrivalAirport?.locationCode ||
                          flight?.arrivalAirport?.code ||
                          flight?.destination}
                      </div>
                      {flight?.departureDateTime && flight?.arrivalDateTime && (
                        <div className="text-lg font-medium text-main">
                          {customTime(flight?.departureDateTime)} -{" "}
                          {customTime(flight?.arrivalDateTime)}
                        </div>
                      )}
                      {flight?.departureAirport?.terminal && (
                        <div className="text-xs text-gray-500">
                          Terminal: {flight?.departureAirport?.terminal}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Selected Bundle Details */}
            {selectedBundles.map((bundle, index) => (
              <div
                key={index}
                className="p-6 bg-white rounded-2xl border border-gray-200 shadow-sm"
              >
                {bundle?.applicableOndSequence === "1" ? (
                  <div className="flex justify-between items-center mb-2">
                    <h2 className="flex items-center mb-4 text-xl font-semibold text-gray-900">
                      <FlightLand className="mr-2 w-5 h-5 text-main" />
                      Selected Inbound Bundle:{" "}
                      {bundle?.bundleName || selectedBundle?.name}
                    </h2>
                  </div>
                ) : (
                  <div className="flex justify-between items-center mb-2">
                    <h2 className="flex items-center mb-4 text-xl font-semibold text-gray-900">
                      <FlightLand className="mr-2 w-5 h-5 text-main" />
                      Selected Outbound Bundle:{" "}
                      {bundle?.bundleName || selectedBundle?.name}
                    </h2>
                  </div>
                )}

                <div className="p-6 rounded-lg border border-main-800">
                  <div className="flex justify-between items-center mb-4">
                    <div>
                      <h3 className="text-lg font-medium text-gray-900">
                        {bundle?.bundleName || selectedBundle?.name}
                      </h3>
                      <p className="text-sm text-gray-600">
                        Booking Class:{" "}
                        {bundle?.bookingClasses ||
                          selectedBundle?.bookingClasses}
                      </p>
                    </div>
                    {/* <div className="text-right">
                      <div className="text-xl font-semibold text-main">
                        +
                        {formatCurrency(
                          bundleFee?.amount ||
                            bundle?.perPaxBundledFee ||
                            selectedBundle?.price,
                          bundleFee?.currency || "USD"
                        )}
                      </div>
                      <div className="text-sm text-gray-600">Bundle Fee</div>
                    </div> */}
                  </div>

                  {/* Included Services */}
                  <div className="mb-4">
                    <h4 className="mb-2 text-sm font-medium text-gray-700">
                      Included Services:
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {(
                        bundle?.includedServices ||
                        selectedBundle?.includedServices ||
                        []
                      ).map((service, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-3 py-1 text-xs font-medium rounded-full text-main bg-main-100"
                        >
                          {getBundleIcon(service)}
                          <span className="ml-1">
                            {service.replace("_", " ")}
                          </span>
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Bundle Description */}
                  <div className="text-sm text-gray-600">
                    <h4 className="mb-2 font-medium text-gray-700">
                      What's included:
                    </h4>
                    {(bundle?.description || selectedBundle?.description || "")
                      .split("\n")
                      .map(
                        (line, index) =>
                          line.trim() && (
                            <div key={index} className="flex items-start mb-1">
                              <CheckCircle className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                              <span>{line.trim()}</span>
                            </div>
                          )
                      )}
                  </div>
                </div>
              </div>
            ))}

            {/* Flexible Fare Benefits */}
            {pricingInfo?.flexiFares?.flexiFares &&
              pricingInfo.flexiFares.flexiFares.length > 0 && (
                <div className="p-6 bg-white rounded-2xl border border-gray-200 shadow-sm">
                  <h2 className="flex items-center mb-4 text-xl font-semibold text-gray-900">
                    <Refresh className="mr-2 w-5 h-5 text-main" />
                    Flexible Fare Benefits
                  </h2>

                  <div className="flex items-center mb-4 space-x-4">
                    <div className="flex items-center">
                      <AccessTime className="mr-1 w-4 h-4 text-main" />
                      <span className="text-sm text-gray-600">
                        Outbound:{" "}
                        {pricingInfo.flexiFares.outBoundFlexiAvailable
                          ? "Available"
                          : "Not Available"}
                      </span>
                    </div>
                    <div className="flex items-center">
                      <AccessTime className="mr-1 w-4 h-4 text-main" />
                      <span className="text-sm text-gray-600">
                        Inbound:{" "}
                        {pricingInfo.flexiFares.inBoundFlexiAvailable
                          ? "Available"
                          : "Not Available"}
                      </span>
                    </div>
                  </div>

                  {pricingInfo.flexiFares.flexiFares.map((flexiFare, index) => (
                    <div
                      key={index}
                      className="p-4 mb-4 rounded-lg border bg-amber-50/30 border-main-800"
                    >
                      <div className="flex justify-between items-center mb-3">
                        <h3 className="font-medium text-gray-900">
                          {flexiFare.flexibilityDescription} -{" "}
                          {flexiFare.applicableJourneyType}
                        </h3>
                        {flexiFare.flexiFareAmount &&
                          flexiFare.flexiFareAmount.amount > 0 && (
                            <span className="text-sm font-medium text-main">
                              +
                              {formatCurrency(
                                flexiFare.flexiFareAmount.amount,
                                flexiFare.flexiFareAmount.currency
                              )}
                            </span>
                          )}
                      </div>

                      <div className="space-y-2">
                        {flexiFare.allowedFlexiOperations?.map(
                          (operation, opIndex) => (
                            <div key={opIndex} className="flex items-start">
                              <CheckCircle className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                              <div className="text-sm text-gray-700">
                                <span className="font-medium">
                                  {operation.numberOfAllowedOperations}
                                </span>{" "}
                                {operation.allowedOperationName}
                                <div className="mt-1 text-xs text-gray-500">
                                  Cutoff:{" "}
                                  {Math.floor(
                                    operation.flexiOperationCutoverTimeInMinutes /
                                      60
                                  )}
                                  h{" "}
                                  {operation.flexiOperationCutoverTimeInMinutes %
                                    60}
                                  m before departure
                                </div>
                              </div>
                            </div>
                          )
                        )}
                      </div>

                      {flexiFare.flexiRuleCode && (
                        <div className="mt-3 text-xs text-gray-500">
                          Rule Code: {flexiFare.flexiRuleCode}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
          </div>

          {/* Price Summary Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-8 p-6 bg-white rounded-2xl border border-gray-200 shadow-sm">
              <h3 className="flex items-center mb-6 text-xl font-semibold text-gray-900">
                <MonetizationOn className="mr-2 w-5 h-5 text-main-800" />
                Final Price Summary
              </h3>

              <div className="space-y-4 text-black">
                {/* Base Fare */}
                {bundlePricing?.pricing?.totals?.baseFare?.amount && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Base Fare</span>
                    <span className="font-medium">
                      {formatCurrency(
                        bundlePricing?.pricing?.totals?.baseFare.amount,
                        bundlePricing?.pricing?.totals?.baseFare?.currency
                      )}{" "}
                    </span>
                  </div>
                )}

                {/* Passenger Fees */}
                {bundlePricing?.pricing?.passengers?.map(
                  (passenger, passengerIndex) => (
                    <div key={passengerIndex}>
                      {passenger.fees?.map((fee, feeIndex) => (
                        <div key={feeIndex} className="flex justify-between">
                          <span className="text-gray-600">
                            {fee.code} - {passenger.passengerType?.code}
                          </span>
                          <span className="font-medium text-main-900">
                            {formatCurrency(fee.amount, fee.currency)}
                          </span>
                        </div>
                      ))}
                    </div>
                  )
                )}

                {/* Taxes & Fees */}
                <div className="flex justify-between">
                  <div>
                    <span className="text-gray-600">Taxes & Charges:</span>
                  </div>
                  <span className="font-medium text-main-900">
                    {bundlePricing?.pricing?.totals?.totalFare?.currency}{" "}
                    {formatNumber(totalTax)}
                  </span>
                </div>

                <hr />
                <div className="flex justify-between mt-2 space-y-2">
                  <div>
                    <span className="text-gray-600">Service Charge</span>
                  </div>
                  <span className="font-medium text-main-900">
                    {bundlePricing?.pricing?.totals?.totalFare?.currency}{" "}
                    {formatNumber(charges || 0)}
                  </span>
                </div>

                <hr className="border-gray-200" />

                {/* Total */}
                <div className="flex justify-between items-center">
                  <span className="text-lg font-semibold text-gray-900">
                    Total Amount
                  </span>
                  <div className="text-right">
                    <div className="text-2xl font-semibold text-main-900">
                      {bundlePricing?.pricing?.totals?.totalFare?.currency}{" "}
                      {formatNumber(totalPrice())}
                    </div>
                  </div>
                </div>

                {/* Proceed to Booking Button */}
                <button
                  onClick={handleProceedToBooking}
                  className="flex justify-center items-center p-2 space-x-2 w-full font-semibold text-white rounded-lg shadow-lg transition-colors duration-200 bg-main-900 hover:bg-main-900 hover:shadow-xl"
                >
                  <span>Proceed to Booking</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PriceWithAncillaries;
